import * as R from "ramda";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { FilterNames } from "../../types/filter";
import { TimeRange } from "../../utils/timerange";

type FilterConfig = {
  defaultValue: string | number | boolean | [number, number];
  urlParam: string;
  apiParam: string;
  includeInApi: (value: any) => boolean;
  transform: (value: any) => any;
};

// Centralized filter configuration
// Add new filters by adding a new entry to this configuration object
export const FILTERS_CONFIG = {
  timeRange: {
    defaultValue: "last7Days" as TimeRange,
    urlParam: "time_range",
    apiParam: "time_range",
    includeInApi: R.T, // Always include in API
    transform: R.identity
  },
  sentiment: {
    defaultValue: "all",
    urlParam: "sentiment",
    apiParam: "sentiment",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  relevance: {
    defaultValue: "all",
    urlParam: "relevance",
    apiParam: "relevance",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  risk: {
    defaultValue: "all",
    urlParam: "ra_overall_indicator",
    apiParam: "ra_overall_indicator",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  protocol: {
    defaultValue: "all",
    urlParam: "sc_name",
    apiParam: "sc_name",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  user: {
    defaultValue: "all",
    urlParam: "user_email",
    apiParam: "user_email",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  processDecision: {
    defaultValue: "DO_PROCESS",
    urlParam: "pp_process_decision",
    apiParam: "pp_process_decision",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  callType: {
    defaultValue: "external",
    urlParam: "call_type",
    apiParam: "call_type",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  search: {
    defaultValue: "",
    urlParam: "search",
    apiParam: "search",
    includeInApi: Boolean,
    transform: R.identity
  },
  reasonCode: {
    defaultValue: "all",
    urlParam: "pp_reason_code",
    apiParam: "pp_reason_code",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  },
  // Special case for score range (has two parameters)
  scoreRange: {
    defaultValue: [0, 5] as [number, number],
    minScore: {
      defaultValue: 0,
      urlParam: "min_score",
      apiParam: "min_score",
      includeInApi: (value: number) => value > 0,
      transform: R.identity
    },
    maxScore: {
      defaultValue: 5,
      urlParam: "max_score",
      apiParam: "max_score",
      includeInApi: (value: number) => value < 5,
      transform: R.identity
    }
  },
  scoredStatus: {
    defaultValue: "all",
    urlParam: "scored_status",
    apiParam: "scored_status",
    includeInApi: R.complement(R.equals("all")),
    transform: R.identity
  }
};

export interface FiltersState {
  timeRange: TimeRange;
  sentiment: string;
  relevance: string;
  risk: string;
  protocol: string;
  user: string;
  processDecision: string;
  callType: string;
  search: string;
  reasonCode: string;
  scoreRange: [number, number];
  scoredStatus: string;
}

interface UseFiltersOptions {
  defaults?: Partial<FiltersState>;
}

export const mapConfigToDefaults = (config: typeof FILTERS_CONFIG, providedDefaults?: Partial<FiltersState>): FiltersState => {
  const builtInDefaults = R.mapObjIndexed(
    (config: FilterConfig) => config.defaultValue,
    R.omit(["scoreRange"], config)
  ) as Omit<FiltersState, "scoreRange">;

  return {
    ...builtInDefaults,
    scoreRange: config.scoreRange.defaultValue,
    ...providedDefaults
  } as FiltersState;
};

// Process regular filters for API params
export const createRegularApiFilters = (
  filters: FiltersState,
  config: typeof REGULAR_FILTERS
): Record<string, any> => {
  const apiFilters: Record<string, any> = {};

  R.forEachObjIndexed((config, key) => {
    const filterKey = key as keyof Omit<FiltersState, "scoreRange">;
    const value = filters[filterKey];

    if (config.includeInApi(value)) {
      apiFilters[config.apiParam] = config.transform(value);
    }
  }, config);

  return apiFilters;
};

// Process score range for API params
export const createScoreRangeApiFilters = (
  scoreRange: [number, number],
  scoreConfig: typeof FILTERS_CONFIG.scoreRange
): Record<string, any> => {
  const apiFilters: Record<string, any> = {};
  const [minScore, maxScore] = scoreRange;

  if (scoreConfig.minScore.includeInApi(minScore)) {
    apiFilters[scoreConfig.minScore.apiParam] =
      scoreConfig.minScore.transform(minScore);
  }

  if (scoreConfig.maxScore.includeInApi(maxScore)) {
    apiFilters[scoreConfig.maxScore.apiParam] =
      scoreConfig.maxScore.transform(maxScore);
  }

  return apiFilters;
};

// Combine API filters without conditional branching
export const createApiFiltersFromState = (
  filters: FiltersState,
  config: typeof FILTERS_CONFIG
): Record<string, any> => {
  // Process regular filters
  const regularApiFilters = createRegularApiFilters(
    filters,
    REGULAR_FILTERS
  );

  // Process score range separately
  const scoreRangeApiFilters = createScoreRangeApiFilters(
    filters.scoreRange,
    config.scoreRange
  );

  // Combine results
  return {
    ...regularApiFilters,
    ...scoreRangeApiFilters
  };
};

// Regular filters configuration (everything except scoreRange)
export const REGULAR_FILTERS = R.omit(["scoreRange"], FILTERS_CONFIG);

// Process regular filters from URL params
export const getRegularFiltersFromParams = (
  searchParams: URLSearchParams,
  defaultValues: FiltersState
): Omit<FiltersState, "scoreRange"> => {
  return R.mapObjIndexed((config: FilterConfig, key: string) => {
    const urlParam = config.urlParam;
    const urlValue = searchParams.get(urlParam);
    const defaultValue = defaultValues[key as keyof Omit<FiltersState, "scoreRange">];

    return urlValue !== null ? urlValue : defaultValue;
  }, REGULAR_FILTERS) as Omit<FiltersState, "scoreRange">;
};

// Process score range filter from URL params
export const getScoreRangeFromParams = (
  scoreConfig: typeof FILTERS_CONFIG.scoreRange,
  searchParams: URLSearchParams,
  defaultRange: [number, number]
): [number, number] => {
  const minScoreParam = searchParams.get(scoreConfig.minScore.urlParam);
  const maxScoreParam = searchParams.get(scoreConfig.maxScore.urlParam);

  const minScore = minScoreParam !== null
    ? parseFloat(minScoreParam)
    : defaultRange[0];

  const maxScore = maxScoreParam !== null
    ? parseFloat(maxScoreParam)
    : defaultRange[1];

  return [minScore, maxScore];
};

// Combine filters without any conditional branching
export const getFiltersFromURLParams = (
  searchParams: URLSearchParams,
  defaultValues: FiltersState
): FiltersState => {
  // Get regular filters
  const regularFilters = getRegularFiltersFromParams(searchParams, defaultValues);

  // Get score range separately
  const scoreRange = getScoreRangeFromParams(
    FILTERS_CONFIG.scoreRange,
    searchParams,
    defaultValues.scoreRange
  );

  // Combine them
  return {
    ...regularFilters,
    scoreRange
  };
};

// Custom hook for handling URL parameters
const useFilterParams = (defaultValues: FiltersState) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract filters from URL params
  const getFiltersFromParams = useCallback((): FiltersState => {
    return getFiltersFromURLParams(searchParams, defaultValues);
  }, [searchParams, defaultValues]);

  // Memoize filters to prevent unnecessary rerenders
  const filters = useMemo(() => getFiltersFromParams(), [getFiltersFromParams]);

  // Update a single filter value
  const updateFilter = useCallback((filterName: FilterNames, value: string): void => {
    const newParams = new URLSearchParams(searchParams);
    const config = FILTERS_CONFIG[filterName as keyof typeof FILTERS_CONFIG] as FilterConfig;
    newParams.set(config.urlParam, value);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle score range filter specifically
  const updateScoreRange = useCallback((value: [number, number]): void => {
    const newParams = new URLSearchParams(searchParams);
    const config = FILTERS_CONFIG.scoreRange;
    newParams.set(config.minScore.urlParam, value[0].toString());
    newParams.set(config.maxScore.urlParam, value[1].toString());
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  return {
    filters,
    updateFilter,
    updateScoreRange
  };
};

// Main hook with simplified structure
export const useFilters = (options: UseFiltersOptions = {}) => {
  // Create default values by merging built-in defaults with provided options
  const defaultValues = useMemo<FiltersState>(() => {
    return mapConfigToDefaults(FILTERS_CONFIG, options.defaults);
  }, [options.defaults]);

  // Use the extracted hook for URL parameter handling
  const { filters, updateFilter, updateScoreRange } = useFilterParams(defaultValues);

  // Convert filters to API params format - now much simpler
  const getApiFilters = useCallback(() => {
    return createApiFiltersFromState(filters, FILTERS_CONFIG);
  }, [filters]);

  return {
    filters,
    updateFilter,
    updateScoreRange,
    getApiFilters
  };
};
